/**
 * Community Garden — Onboarding API
 * 
 * GARDEN-136: Main onboarding entry point with step persistence and resumability.
 * Re-exports from components/Onboarding.ts with added state management utilities.
 */

export {
  Onboarding,
  ONBOARDING_STEPS,
  type OnboardingStep
} from './components/Onboarding.js';

export {
  // State management
  STORAGE_KEY,
  PROGRESS_KEY,
  COMPLETED_STEPS_KEY,
  SKIPPED_KEY,
  SETTINGS_KEY,
  STORAGE_VERSION,
  SESSION_EXPIRY_MS,
  saveProgress,
  loadProgress,
  clearProgress,
  markStepCompleted,
  getCompletedSteps,
  isStepCompleted,
  clearCompletedSteps,
  markCompleted,
  hasCompleted,
  getCompletionDate,
  markSkipped,
  wasSkipped,
  clearSkipped,
  canResume,
  getResumeStepIndex,
  saveSettings,
  loadSettings,
  resetAll,
  resetProgressOnly,
  getStateSnapshot,
  shouldAutoStart,
  getInitialStepIndex,
  dispatchStateChange,
  onStateChange,
  // REC-006: Session expiry exports
  isSessionExpired,
  getSessionTimeRemaining,
  type OnboardingProgress,
  type OnboardingSettings,
  type OnboardingStateSnapshot
} from './onboarding-state.js';

import {
  Onboarding,
  ONBOARDING_STEPS,
  getOnboarding as getComponentOnboarding,
} from './components/Onboarding.js';
import * as OnboardingState from './onboarding-state.js';

// REC-006: Track if onboarding has been initialized this session
let _hasInitializedThisSession = false;
let _startTimer: number | null = null;
let _retryTimer: number | null = null;

/* ── Singleton Instance ──────────────────────────────── */

export function getOnboarding(): Onboarding {
  return getComponentOnboarding();
}

/* ── Initialization ──────────────────────────────────── */

/**
 * Initialize onboarding on app load
 * Checks for incomplete onboarding and auto-starts/resumes
 * REC-006: Prevents unnecessary restarts and handles resumability
 */
export function initOnboarding(): void {
  // REC-006: Don't restart if already initialized this session
  if (_hasInitializedThisSession) {
    console.log('[onboarding] Already initialized this session, skipping');
    return;
  }
  
  // Check if should auto-start
  if (!OnboardingState.shouldAutoStart()) {
    _hasInitializedThisSession = true;
    return;
  }

  // REC-006: Mark as initialized to prevent duplicate calls
  _hasInitializedThisSession = true;

  // Wait for UI to be ready
  _startTimer = window.setTimeout(() => {
    _startTimer = null;
    const canvas = document.querySelector('#garden-canvas, canvas');
    if (!canvas) {
      // Retry once after a longer delay
      _retryTimer = window.setTimeout(() => {
        _retryTimer = null;
        if (document.querySelector('#garden-canvas, canvas')) {
          getOnboarding().start();
        }
      }, 2000);
      return;
    }
    
    getOnboarding().start();
  }, 1000);
}

/** Tear down pending first-run work when leaving a garden route. */
export function destroyOnboarding(): void {
  if (_startTimer !== null) window.clearTimeout(_startTimer);
  if (_retryTimer !== null) window.clearTimeout(_retryTimer);
  _startTimer = null;
  _retryTimer = null;
  _hasInitializedThisSession = false;
  getOnboarding().destroy();
}

/**
 * Resume onboarding from settings (user-initiated)
 * REC-006: Handles session expiry and validates resume state
 */
export function resumeOnboarding(): boolean {
  if (OnboardingState.hasCompleted()) {
    console.log('[onboarding] Already completed, cannot resume');
    return false;
  }

  // REC-006: Check for session expiry
  if (OnboardingState.isSessionExpired()) {
    console.log('[onboarding] Session expired, clearing progress');
    OnboardingState.clearProgress();
    OnboardingState.clearCompletedSteps();
  }

  if (!OnboardingState.canResume()) {
    console.log('[onboarding] No progress to resume, starting fresh');
    // Allow starting fresh if no progress to resume
    OnboardingState.clearSkipped();
    getOnboarding().start();
    return true;
  }

  OnboardingState.clearSkipped();
  getOnboarding().start();
  return true;
}

/**
 * Start fresh onboarding (reset and start)
 */
export function startFreshOnboarding(): void {
  OnboardingState.resetAll();
  getOnboarding().start();
}

/**
 * Check if onboarding resume is available (for settings UI)
 */
export function isResumeAvailable(): boolean {
  return !OnboardingState.hasCompleted() && OnboardingState.canResume();
}

/* ── Settings Integration ─────────────────────────────── */

/**
 * Reset onboarding from settings
 */
export function resetOnboardingFromSettings(): void {
  OnboardingState.resetAll();
  console.log('[onboarding] Reset from settings');
}

/**
 * Get onboarding status for settings display
 * REC-006: Includes session expiry information
 */
export function getOnboardingStatus(): {
  completed: boolean;
  canResume: boolean;
  isSkipped: boolean;
  progressPercent: number;
  completedSteps: number;
  totalSteps: number;
  lastUpdated: string | null;
  // REC-006: Session expiry info
  isSessionExpired: boolean;
  sessionTimeRemaining: number;
} {
  const snapshot = OnboardingState.getStateSnapshot(ONBOARDING_STEPS.length);
  const completedSteps = snapshot.completedSteps.length;
  const progressPercent = Math.round((completedSteps / ONBOARDING_STEPS.length) * 100);

  return {
    completed: snapshot.hasCompleted,
    canResume: snapshot.canResume,
    isSkipped: snapshot.isSkipped,
    progressPercent,
    completedSteps,
    totalSteps: ONBOARDING_STEPS.length,
    lastUpdated: snapshot.lastUpdated,
    // REC-006: Session expiry info
    isSessionExpired: snapshot.isSessionExpired,
    sessionTimeRemaining: snapshot.sessionTimeRemaining
  };
}

/* ── Backwards Compatibility ─────────────────────────── */

// Singleton export with lazy initialization
// Uses prototype chaining to pass instanceof checks
function createOnboardingProxy(): Onboarding {
  const handler = {
    get(target: any, prop: string | symbol) {
      const instance = getOnboarding();
      const value = (instance as any)[prop];
      return typeof value === 'function' ? value.bind(instance) : value;
    }
  };
  
  const proxy = new Proxy(function() {}, handler);
  Object.setPrototypeOf(proxy, Onboarding.prototype);
  return proxy as unknown as Onboarding;
}

/**
 * Backwards-compatible singleton instance
 * Use getOnboarding() for full control
 */
export const onboarding: Onboarding = createOnboardingProxy();

// Re-export state namespace
export { OnboardingState };
